import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Feather, Globe2, Landmark } from "lucide-react";
import { books, allImprints } from "../data/books";
import { BookCard } from "../components/BookCard";
import { SEO } from "../components/SEO";
import { InstitutionalModal } from "../components/InstitutionalModal";

const featured = books.slice(0, 4);
const heroBook = books.find((b) => b.slug === "i-am-india") || books[0];
const spotlight = books.find((b) => b.slug === "i-am-india");
const bharat = books.filter((b) => b.imprint === "Bharat Icon Series" && b.slug !== "i-am-india");
const houseList = books.filter((b) => b.imprint === "Conlang House List");
const fellows = books.filter((b) => b.imprint === "Conlang Fellows");
const selfHelp = books.filter((b) => b.genres.includes("Self-Help"));
const womenLeadership = books.filter((b) => b.genres.includes("Women Leadership"));
const society = books.filter((b) => b.genres.includes("Society"));
const geoPolitics = books.filter((b) => b.genres.includes("Geo-Politics"));
const children = books.filter((b) => b.genres.includes("Children"));
const business = books.filter((b) => b.genres.includes("Business"));

export default function Home() {
  const [giftOpen, setGiftOpen] = React.useState(false);
  return (
    <div data-testid="home-page">
      <SEO
        title=""
        path="/"
        description="Conlang Book House (CBH) — a premium Indian publisher of biographies, wisdom, self-help, women leadership, and geo-politics books from Bharat. Powered by Conlang International."
        image={heroBook.cover}
        imageAlt={heroBook.coverAlt}
        keywords="Conlang Book House, CBH, Indian publisher, biographies, Bharat Icon Series, self-help, women leadership, geopolitics"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Conlang Book House",
          url: "https://conlangbookhouse.com",
          publisher: { "@type": "Organization", name: "Conlang Book House" },
          potentialAction: {
            "@type": "SearchAction",
            target: "https://conlangbookhouse.com/books?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay" data-testid="home-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32 grid grid-cols-12 gap-6 lg:gap-12 items-start">
          <div className="col-span-12 lg:col-span-7">
            <p className="ornament text-[11px] uppercase tracking-[0.32em] mb-8">
              Est. Bharat &middot; Vol. I
            </p>
            <h1 className="font-editorial text-[13vw] sm:text-8xl lg:text-[8.5rem] leading-[0.88] tracking-tighter text-ink text-balance">
              Where <span className="italic text-crimson">ideas</span> become <br className="hidden md:block"/>
              institutions.
            </h1>
            <p className="mt-10 max-w-xl text-lg leading-relaxed text-ink-soft">
              Conlang Book House publishes the biographies, wisdom traditions, and civic
              literature that shape modern Bharat &mdash; and translates them, unapologetically,
              for the world.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/books"
                data-testid="hero-cta-books"
                className="group inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
              >
                Explore the Catalogue
                <ArrowRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
              </Link>
              <Link
                to="/publish"
                data-testid="hero-cta-publish"
                className="inline-flex items-center gap-3 border border-crimson/40 text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson/5 transition-colors duration-500"
              >
                Publish With Us
              </Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[420px]">
              <img
                src={heroBook.cover}
                alt={heroBook.coverAlt}
                className="w-full book-cover-3d relative z-10"
              />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 border border-gold/40 hidden md:block" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-crimson/5 hidden md:block" />
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-crimson/15 bg-ivory-soft/50">
          <div className="overflow-hidden mask-fade-x">
            <div className="flex whitespace-nowrap animate-marquee">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center gap-16 py-4 pr-16">
                  {[
                    "Nine Titles. Four Imprints.",
                    "Biography · Wisdom · Civic Analysis",
                    "Powered by Conlang International",
                    "Curated House List",
                    "Bharat Icon Series",
                    "Conlang Fellows",
                    "Idea-to-Impact Ecosystem",
                  ].map((t, j) => (
                    <span
                      key={`${i}-${j}`}
                      className="text-[11px] uppercase tracking-[0.32em] text-ink-muted"
                    >
                      ✦ {t}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEW RELEASE SPOTLIGHT — I Am India */}
      {spotlight && (
        <section
          className="relative overflow-hidden grain-overlay bg-ivory-soft border-y border-crimson/15"
          data-testid="home-spotlight"
          style={{ backgroundColor: spotlight.palette.bg }}
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-28 grid grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="col-span-12 lg:col-span-5">
              <Link to={`/books/${spotlight.slug}`} className="block group">
                <img
                  src={spotlight.cover}
                  alt={spotlight.coverAlt}
                  className="w-full max-w-[420px] book-cover-3d group-hover:scale-[1.02] transition-transform duration-700"
                />
              </Link>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <p
                className="ornament text-[11px] uppercase tracking-[0.32em] mb-6"
                style={{ color: spotlight.palette.accent }}
              >
                New Release · Flagship Volume
              </p>
              <h2
                className="font-editorial text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.9] tracking-tight text-balance"
                style={{ color: spotlight.palette.ink }}
              >
                {spotlight.title}
                <span className="block editorial-italic text-3xl md:text-4xl mt-4" style={{ color: spotlight.palette.accent }}>
                  {spotlight.subtitle}
                </span>
              </h2>
              <p className="mt-8 max-w-xl text-lg leading-relaxed" style={{ color: spotlight.palette.ink, opacity: 0.82 }}>
                {spotlight.editorialNote}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to={`/books/${spotlight.slug}`}
                  data-testid="spotlight-cta-detail"
                  className="inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
                >
                  Open the Book →
                </Link>
                <a
                  href={spotlight.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="spotlight-cta-amazon"
                  className="inline-flex items-center gap-3 bg-ink text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-black transition-colors duration-500"
                >
                  <span className="text-gold-light font-editorial">a</span>
                  Buy on Amazon
                </a>
                <button
                  onClick={() => setGiftOpen(true)}
                  data-testid="spotlight-cta-gifting"
                  className="inline-flex items-center gap-3 border border-crimson/40 text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson hover:text-ivory transition-colors duration-500"
                >
                  Order for Corporate Gifting →
                </button>
                <p className="text-[11px] uppercase tracking-[0.28em] opacity-70" style={{ color: spotlight.palette.ink }}>
                  ₹{spotlight.price} · {spotlight.format}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="py-24 lg:py-32" data-testid="home-featured">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Featured</p>
              <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-none tracking-tight">
                This Season&rsquo;s <em className="text-crimson not-italic serif-display">Volumes</em>
              </h2>
            </div>
            <Link
              to="/books"
              className="text-sm uppercase tracking-[0.24em] text-ink hover:text-crimson link-underline"
              data-testid="featured-view-all"
            >
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {featured.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* BHARAT ICON SERIES */}
      <section className="py-24 lg:py-32 bg-ink text-ivory grain-overlay relative" data-testid="home-bharat-icons">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-16">
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ Bharat Icon Series</p>
            <h2 className="font-editorial text-5xl md:text-7xl leading-none tracking-tight text-balance">
              The figures who <em className="text-gold-light not-italic">built the republic.</em>
            </h2>
            <p className="mt-8 max-w-2xl text-ivory/70 text-lg leading-relaxed">
              Deluxe hardcover biographies of the men and women whose lives are, in effect,
              chapters of the country itself.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {bharat.map((b, i) => (
              <Link
                to={`/books/${b.slug}`}
                key={b.slug}
                data-testid={`bharat-icon-${b.slug}`}
                className="group opacity-0 animate-fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[2/3] overflow-hidden" style={{ backgroundColor: b.palette.bg }}>
                  <img src={b.cover} alt={b.coverAlt} className="w-full h-full object-contain group-hover:scale-105 duration-700" />
                </div>
                <p className="mt-6 text-[10px] uppercase tracking-[0.32em] text-gold-light">Bharat Icon</p>
                <h3 className="font-editorial text-2xl mt-2 group-hover:text-gold-light transition-colors duration-500">{b.title}</h3>
                <p className="text-ivory/60 italic text-sm mt-2">{b.subtitle}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOUSE LIST */}
      <section className="py-24 lg:py-32" data-testid="home-houselist">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div className="max-w-xl">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Conlang House List</p>
              <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-none tracking-tight text-balance">
                Frontlist, <em className="text-crimson not-italic">curated.</em>
              </h2>
            </div>
            <Link to="/books" className="text-sm uppercase tracking-[0.24em] text-ink hover:text-crimson link-underline">
              Full catalogue →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {houseList.slice(0, 4).map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CONLANG FELLOWS + QUOTE */}
      <section className="bg-ivory-soft py-24 lg:py-32" data-testid="home-fellows">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Conlang Fellows</p>
            <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-none tracking-tight text-balance mb-8">
              Scholars, <em className="text-crimson not-italic">at the desk.</em>
            </h2>
            <p className="text-ink-muted text-lg leading-relaxed mb-8">
              A quiet imprint for civil servants, historians, and public intellectuals who
              have spent decades in one subject and, finally, one book.
            </p>
            <Link to="/books?imprint=Conlang%20Fellows" className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-crimson link-underline">
              Meet the Fellows →
            </Link>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              {fellows.map((b, i) => (
                <BookCard key={b.slug} book={b} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SELF-HELP */}
      <section className="py-24 lg:py-32 bg-ivory" data-testid="home-self-help">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 lg:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Self-Help</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                Everyday books, <em className="text-crimson not-italic">everyday practice.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-lg leading-relaxed text-ink-muted">
                Conlang Wellbeing publishes short, sharp, deeply-researched self-help volumes —
                grounded in Indian contemplative traditions and modern behavioural science. Written
                to be read in a weekend and lived across a decade.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {selfHelp.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Conlang%20Wellbeing"
              data-testid="home-self-help-view-all"
              className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
            >
              Explore all Self-Help titles →
            </Link>
          </div>
        </div>
      </section>

      {/* WOMEN LEADERSHIP */}
      <section className="py-24 lg:py-32 bg-ivory-soft grain-overlay" data-testid="home-women-leadership">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Women Leadership</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                The women who <em className="text-crimson not-italic">moved the country.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-lg leading-relaxed text-ink-muted">
                A biography series for the scientists, activists, mountaineers, and physicians whose
                lives are, in effect, chapters of modern India — reconstructed with the archival
                seriousness CBH brings to every icon.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {womenLeadership.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Bharat%20Women%20Icons"
              data-testid="home-women-leadership-view-all"
              className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
            >
              Explore all Women Leadership titles →
            </Link>
          </div>
        </div>
      </section>

      {/* SOCIETY (positioned directly below Women Leadership) */}
      <section className="py-24 lg:py-32 bg-ivory" data-testid="home-society">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Society</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                The country, <em className="text-crimson not-italic">up close.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-lg leading-relaxed text-ink-muted">
                Conlang Society publishes the deeply-reported civic literature the country
                does not yet write about itself — seva, gram panchayats, resident welfare
                associations, migration corridors, and the quiet infrastructure of everyday
                Indian life.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {society.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Conlang%20Society"
              data-testid="home-society-view-all"
              className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
            >
              Explore all Society titles →
            </Link>
          </div>
        </div>
      </section>

      {/* GEO-POLITICS */}
      <section className="py-24 lg:py-32 bg-ink text-ivory grain-overlay relative" data-testid="home-geo-politics">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-4">§ Geo-Politics</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight text-balance">
                The world, <em className="text-gold-light not-italic">from Delhi.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-lg leading-relaxed text-ivory/70">
                The Geopolitics Series reads foreign policy the way it is actually written today —
                through borders, trade cables, diasporas, and the quiet chessboards of the Himalayan
                frontier.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {geoPolitics.map((b, i) => (
              <Link
                to={`/books/${b.slug}`}
                key={b.slug}
                data-testid={`geo-politics-${b.slug}`}
                className="group opacity-0 animate-fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[2/3] overflow-hidden" style={{ backgroundColor: b.palette.bg }}>
                  <img src={b.cover} alt={b.coverAlt} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 duration-700" />
                </div>
                <p className="mt-5 text-[10px] uppercase tracking-[0.28em] text-gold-light">{b.imprint}</p>
                <h3 className="font-editorial text-xl md:text-2xl mt-2 group-hover:text-gold-light transition-colors duration-500">{b.title}</h3>
                <p className="text-ivory/60 italic text-sm mt-2">{b.subtitle}</p>
              </Link>
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Conlang%20Geopolitics"
              data-testid="home-geo-politics-view-all"
              className="text-sm uppercase tracking-[0.24em] text-gold-light link-underline"
            >
              Explore all Geo-Politics titles →
            </Link>
          </div>
        </div>
      </section>

      {/* CHILDREN */}
      <section className="py-24 lg:py-32 bg-ivory" data-testid="home-children">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Children</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                Little readers, <em className="text-crimson not-italic">big ideas.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-lg leading-relaxed text-ink-muted">
                Illustrated books for readers aged 6–14 — on India, democracy, AI, and the
                everyday civic imagination of the country they are about to inherit. Warm,
                curious, and never talking down.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {children.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Conlang%20Little%20Readers"
              data-testid="home-children-view-all"
              className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
            >
              Explore all Children titles →
            </Link>
          </div>
        </div>
      </section>

      {/* BUSINESS */}
      <section className="py-24 lg:py-32 bg-ivory-soft grain-overlay" data-testid="home-business">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Business</p>
              <h2 className="font-editorial text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.92] tracking-tight text-balance">
                The country&rsquo;s next <em className="text-crimson not-italic">balance sheet.</em>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-lg leading-relaxed text-ink-muted">
                Conlang Business publishes practical, field-first volumes for founders,
                operators, negotiators, and family-enterprise leaders — grounded in Indian
                data, deal-rooms, and shop-floors, and written for use before Monday&rsquo;s
                meeting.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
            {business.map((b, i) => (
              <BookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/books?imprint=Conlang%20Business"
              data-testid="home-business-view-all"
              className="text-sm uppercase tracking-[0.24em] text-crimson link-underline"
            >
              Explore all Business titles →
            </Link>
          </div>
        </div>
      </section>

      {/* YLGF Spotlight */}
      <section className="bg-ink text-ivory grain-overlay relative overflow-hidden" data-testid="home-ylgf">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-28 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-6">§ Fellowship · Now Open</p>
            <h2 className="font-editorial text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.9] tracking-tighter text-balance">
              Young Leadership <em className="text-gold-light not-italic">&amp; Governance Fellowship.</em>
            </h2>
            <p className="editorial-italic text-2xl mt-6 text-ivory/80">Document. Connect. Lead. Publish.</p>
            <p className="mt-8 max-w-2xl text-ivory/70 text-lg leading-relaxed">
              India&rsquo;s largest leadership &amp; democratic documentation fellowship &mdash; 10 fellows per district, an 8&ndash;12 week immersion inside a real constituency, and a published book bearing your name.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/ylgf" data-testid="home-ylgf-explore" className="inline-flex items-center gap-3 bg-gold-light text-ink px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-ivory transition-colors duration-500">
                Explore YLGF →
              </Link>
              <Link to="/ylgf#apply" data-testid="home-ylgf-apply" className="inline-flex items-center gap-3 border border-gold-light/40 text-gold-light px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-gold-light hover:text-ink transition-colors duration-500">
                Apply Now
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="grid grid-cols-3 gap-4">
              {[["10", "Fellows / district"],["8-12", "Weeks"],["11", "Deliverables"]].map(([n, l]) => (
                <div key={l} className="text-center border border-gold-light/20 p-4">
                  <p className="font-editorial text-4xl text-gold-light leading-none">{n}</p>
                  <p className="mt-3 text-[9px] uppercase tracking-[0.28em] text-ivory/60">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE / MANIFESTO */}
      <section className="py-32 grain-overlay" data-testid="home-manifesto">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="ornament text-[11px] uppercase tracking-[0.32em] mb-8 justify-center">The CBH Manifesto</p>
          <blockquote className="font-editorial italic text-4xl md:text-6xl leading-tight text-ink text-balance">
            &ldquo;We publish books that <span className="text-crimson">outlast</span> the news cycle
            &mdash; and often, outlast the people they are written about.&rdquo;
          </blockquote>
          <p className="mt-10 text-[11px] uppercase tracking-[0.32em] text-ink-muted">— The Editorial Board</p>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-crimson text-ivory grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-editorial text-4xl md:text-6xl leading-none tracking-tight text-balance">
              You have a book <em className="text-gold-light not-italic">in you.</em>
            </h2>
            <p className="mt-6 max-w-xl text-ivory/80 text-lg leading-relaxed">
              Whether it&rsquo;s a memoir, a policy tract, or a decade of scholarship — CBH
              publishes across two tracks and one very high bar.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end">
            <Link
              to="/publish"
              className="inline-flex items-center gap-3 bg-ivory text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-gold-light hover:text-ink transition-colors duration-500"
              data-testid="home-cta-publish"
            >
              Submit Your Manuscript →
            </Link>
          </div>
        </div>
      </section>
      {/* IMPRINTS — moved to just above footer */}
      <section className="bg-ivory-soft py-24 lg:py-32 grain-overlay" data-testid="home-imprints">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="max-w-2xl mb-20">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Our Imprints</p>
            <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-none tracking-tight text-balance">
              Four houses under <em className="text-crimson not-italic">one roof.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-crimson/15">
            {[
              { icon: BookOpen, name: "Conlang House List", desc: "The curated frontlist — books commissioned, edited, and produced entirely in-house." },
              { icon: Landmark, name: "Bharat Icon Series", desc: "Definitive biographies of the figures who built modern Bharat." },
              { icon: Feather, name: "Conlang Fellows", desc: "Long-form works by scholars, civil servants, and public intellectuals." },
              { icon: Globe2, name: "Conlang International", desc: "Global co-editions, translations, and diaspora scholarship." },
            ].map(({ icon: Icon, name, desc }, i) => (
              <div key={i} className="bg-ivory p-10 lg:p-14 group cursor-default">
                <Icon strokeWidth={1} className="w-10 h-10 text-crimson mb-8" />
                <h3 className="font-editorial text-3xl text-ink mb-4">{name}</h3>
                <p className="text-ink-muted leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Gifting modal (opens from I Am India spotlight) */}
      <InstitutionalModal
        open={giftOpen}
        onClose={() => setGiftOpen(false)}
        defaultType="corporate_gifting"
        book={spotlight}
      />
    </div>
  );
}
